import Link from 'next/link';
import { useRouter } from 'next/router';
import { type ElementType, type MouseEvent, memo, type PointerEvent, useCallback, useMemo } from 'react';

import { useTrackSidebarInteraction } from '@air/analytics';
import { type SavedFilter } from '@air/api/types';
import { BoardMove, Clock, Lifeline, Pencil, Plus, Search, Star, Tag, TriangleDown, Uploads } from '@air/next-icons';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  type DropdownMenuOption,
  DropdownMenuSeparator,
  renderDropdownItems,
} from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { useAirModal } from '@air/provider-modal';

import { CreateSavedFilterModal } from '~/components/SavedFilter/CreateSavedFilterModal';
import { EditSavedFilterModal } from '~/components/SavedFilter/EditSavedFilterModal';
import { Pathnames } from '~/components/v2/constants/pathnames';
import { usePrivateSavedSearchList } from '~/components/v2/hooks/queries/usePrivateSavedSearchList';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';

const SIDEBAR_SEARCH_DROPDOWN_LOCK_TOKEN = 'sidebar-search-dropdown';

import { createSavedSearchRoute } from '~/constants/routes';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getPathWithoutQuery } from '~/utils/PathUtils';
import {
  canCreateSavedFilters,
  canEditSavedFilters,
  canViewSavedFilters,
} from '~/utils/permissions/workspacePermissions';

type SearchDropdownOption = {
  id: string;
  label: string;
  icon: ElementType;
  pathname: Pathnames;
  trackingId:
    | 'recently_added'
    | 'my_uploads'
    | 'my_favorites'
    | 'recently_modified'
    | 'unused_items'
    | 'untagged_items';
};

const ALL_SEARCH_DROPDOWN_OPTIONS: SearchDropdownOption[] = [
  {
    id: 'recently-added',
    label: 'Recently added',
    icon: Clock,
    pathname: Pathnames.RecentlyAdded,
    trackingId: 'recently_added',
  },
  {
    id: 'my-uploads',
    label: 'My uploads',
    icon: Uploads,
    pathname: Pathnames.MyUploads,
    trackingId: 'my_uploads',
  },
  {
    id: 'my-favorites',
    label: 'My favorites',
    icon: Star,
    pathname: Pathnames.FavoriteItems,
    trackingId: 'my_favorites',
  },
  {
    id: 'recently-modified',
    label: 'Recently modified',
    icon: Lifeline,
    pathname: Pathnames.RecentlyModified,
    trackingId: 'recently_modified',
  },
  {
    id: 'unused-items',
    label: 'Unused items',
    icon: BoardMove,
    pathname: Pathnames.UnusedItems,
    trackingId: 'unused_items',
  },
  {
    id: 'untagged-items',
    label: 'Untagged items',
    icon: Tag,
    pathname: Pathnames.UntaggedItems,
    trackingId: 'untagged_items',
  },
];

export const WorkspaceSidebarNavigationItemSearch = memo(() => {
  const router = useRouter();
  const canvasQueryParams = useMemo(() => getCanvasQueryParams(), [router.asPath]);
  const { trackSidebarInteraction } = useTrackSidebarInteraction();
  const { currentWorkspace } = useCurrentWorkspace();
  const { handleOpenChange: handleLockOpenChange } = useWorkspaceSidebarPushLock({
    token: SIDEBAR_SEARCH_DROPDOWN_LOCK_TOKEN,
  });
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { data: savedSearchList } = usePrivateSavedSearchList(
    {
      workspaceId: currentWorkspace?.id,
      options: { limit: 100, cursor: null, sortBy: 'createdAt' },
    },
    {
      enabled: canViewSavedFilters(permissions),
    },
  );
  const [showCreateSavedFilterModal] = useAirModal(CreateSavedFilterModal);
  const [showEditSavedFilterModal] = useAirModal(EditSavedFilterModal);
  const canAdd = canCreateSavedFilters(permissions);
  const canEdit = canEditSavedFilters(permissions);

  const handleSearchClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      trackSidebarInteraction({ action: 'link_click', type: 'search' });
    },
    [trackSidebarInteraction],
  );

  const handleSearchPointerDown = useCallback((e: PointerEvent) => {
    e.stopPropagation();
  }, []);

  const handleSavedSearchSelect = useCallback(
    (savedSearchId: string) => {
      const route = createSavedSearchRoute(savedSearchId);

      if (getPathWithoutQuery(router.asPath) !== route) {
        router.push({ pathname: route, query: canvasQueryParams });
      }
    },
    [canvasQueryParams, router],
  );

  const handleEdit = useCallback(
    (event: MouseEvent, savedFilter: SavedFilter) => {
      event.preventDefault();
      showEditSavedFilterModal({ savedFilter });
    },
    [showEditSavedFilterModal],
  );

  const handleCreate = useCallback(() => {
    showCreateSavedFilterModal({
      trackLocation: 'side-nav-saved-filters',
    });
  }, [showCreateSavedFilterModal]);

  const isSearchSelected = router.pathname === Pathnames.Search;
  const isDropdownItemSelected = useMemo(
    () => ALL_SEARCH_DROPDOWN_OPTIONS.some((option) => router.pathname === option.pathname),
    [router.pathname],
  );

  const dropdownOptions = useMemo<DropdownMenuOption[]>(() => {
    return ALL_SEARCH_DROPDOWN_OPTIONS.map((option) => ({
      id: option.id,
      label: option.label,
      className: router.pathname === option.pathname ? 'bg-grey-4' : undefined,
      onSelect: () => {
        router.push({ pathname: option.pathname, query: canvasQueryParams });
        trackSidebarInteraction({ action: 'link_click', type: option.trackingId });
      },
      prefix: <option.icon className="size-4 shrink-0" />,
      type: 'item',
    }));
  }, [canvasQueryParams, router, trackSidebarInteraction]);

  const currentPath = getPathWithoutQuery(router.asPath);
  const savedSearches = savedSearchList?.data;
  const hasSavedSearches = !!savedSearches?.length;

  return (
    <DropdownMenu
      align="start"
      onOpenChange={handleLockOpenChange}
      sideOffset={4}
      trigger={
        <div
          className="group/search-trigger flex items-center rounded-8 transition-colors hover:bg-grey-5 data-[selected=true]:bg-grey-5 data-[state=open]:bg-grey-5"
          data-selected={isSearchSelected || isDropdownItemSelected}
        >
          <Link
            className="flex h-8 grow items-center gap-2 truncate rounded-l-8 px-2 text-left font-normal text-14 text-grey-11 no-underline outline-none transition-colors hover:no-underline focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
            data-testid="SIDEBAR_NAVIGATION_ITEM_SEARCH"
            href={{ pathname: Pathnames.Search, query: canvasQueryParams }}
            onClick={handleSearchClick}
            onPointerDown={handleSearchPointerDown}
          >
            <Search className="size-4 shrink-0" />
            Search
          </Link>
          <IconButton
            appearance="ghost"
            className="mr-1 hover:bg-grey-6 group-data-[state=open]/search-trigger:bg-grey-6"
            color="grey"
            icon={TriangleDown}
            label="More search options"
            size="small"
            data-testid="SIDEBAR_NAVIGATION_ITEM_SEARCH_DROPDOWN"
          />
        </div>
      }
    >
      {renderDropdownItems({ options: dropdownOptions })}
      {(hasSavedSearches || canAdd) && <DropdownMenuSeparator />}
      {hasSavedSearches && savedSearches && (
        <>
          <DropdownMenuLabel>Saved searches</DropdownMenuLabel>
          <div className="max-h-[50vh] overflow-y-auto">
            {savedSearches.map((savedSearch) => (
              <DropdownMenuItem
                className={currentPath === createSavedSearchRoute(savedSearch.id) ? 'bg-grey-4' : undefined}
                data-title={savedSearch.name}
                key={savedSearch.id}
                label={savedSearch.name}
                onSelect={() => handleSavedSearchSelect(savedSearch.id)}
                prefix={
                  <div className="flex size-4 items-center justify-center rounded-2 bg-grey-4 text-10">
                    {savedSearch.icon}
                  </div>
                }
                suffix={
                  canEdit && (
                    <IconButton
                      data-testid="SIDEBAR_NAVIGATION_SAVED_SEARCHES_EDIT"
                      appearance="ghost"
                      className="opacity-0 group-hover/dropdown-menu-item:opacity-100"
                      color="grey"
                      data-id={savedSearch.id}
                      data-name={savedSearch.name}
                      icon={Pencil}
                      label="Edit"
                      onClick={(event) => handleEdit(event, savedSearch)}
                      size="small"
                    />
                  )
                }
              />
            ))}
          </div>
        </>
      )}
      {canAdd && (
        <DropdownMenuItem
          data-id="create"
          label="Create a saved search"
          onSelect={handleCreate}
          prefix={<Plus className="size-4" />}
        />
      )}
    </DropdownMenu>
  );
});

WorkspaceSidebarNavigationItemSearch.displayName = 'WorkspaceSidebarNavigationItemSearch';
